import Big from 'big.js';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  BillableIcon,
  Checkbox,
  ClientLink,
  Currency,
  ExportDropdown,
  Hours,
  Icon,
  InternalClientTooltip,
  ProjectLink,
  RouteLink,
  Tooltip,
  YesNoFilter,
} from '~/components';
import { ProjectRoleFiltersBar, ProjectRoleFiltersGroup } from '~/components/filters';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import CurrencyFilter from '~/components/filters/CurrencyFilter';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import TimeFiltersBar from '~/components/filters/TimeFiltersBar';
import TimeFiltersGroup from '~/components/filters/TimeFiltersGroup';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useDocumentTitle,
  useFeatures,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import projectBillingTypes from '~/lookups/project-billing-types.js';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const ProjectInfo = styled.div`
  line-height: 1;
  margin-left: 1rem;
`;

const MemberRole = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
`;

const ClientInfo = styled.div`
  display: flex;
`;

const Warning = styled(Tooltip)`
  margin-right: 1rem;
`;

const Byline = styled.small`
  display: block;
`;

export default function HourlyRevenueByClientAndProject({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();

  const [query, setQuery] = useState({
    report: null,
    status: 'loading',
    loading: {
      table: false,
    },
  });

  const searchParamsConfig = useSearchParamsConfig();

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters(() => ({
    projectBillingTypes: {
      ...searchParamsConfig.projectBillingTypes,
      default: [projectBillingTypes.tm],
    },
  }));
  const memberFilters = useMemberFilters();
  const roleFilters = useProjectRoleFilters();
  const timeFilters = useTimeFilters();

  const [params, setParams] = useState({
    period: null,
    includeMembersAndRolesWithoutTime: false,
    currency: workspace.currency,
    sort: new QuerySort('project:client.name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...roleFilters.filters,
    ...memberFilters.filters,
    ...timeFilters.filters,
  });

  // Init and sync URL search params
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        includeMembersAndRolesWithoutTime: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },
        currency: searchParamsConfig.currency,
        sort: { default: new QuerySort('project:client.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        memberFilters,
        timeFilters,
        roleFilters,
      ],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      includeMembersAndRolesWithoutTime: params.includeMembersAndRolesWithoutTime ? 'true' : undefined,
      currency: params.currency ?? undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, roleFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({
      ...state,
      data,
      status: 'ready',
      loading: { table: false },
    }));
  }, [api, workspace.id, urlSearchParams, report.id]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, table: true } }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleCurrencyChange = (currency) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, summary: true, table: true } }));
    setParams({ ...params, page: 0, currency });
    searchParams.set({ currency });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        {features.multicurrency && (
          <CurrencyFilter value={params.currency} onChange={({ target: { value } }) => handleCurrencyChange(value)} />
        )}

        <YesNoFilter
          icon="filter"
          placeholder="Members and roles without time"
          value={params.includeMembersAndRolesWithoutTime ? 'yes' : 'no'}
          onChange={({ target: { value } }) =>
            handleApplyFilters({ includeMembersAndRolesWithoutTime: value === 'yes' })
          }
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} onCurrencyChange={handleCurrencyChange} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const groups = useMemo(() => {
    return report.records.reduce((a, v) => {
      a[v.project.client.id] = a[v.project.client.id] || {
        client: v.project.client,
        projects: {},
        billableHours: 0,
        nonBillableHours: 0,
        totalHours: 0,
        convertedTotalFees: 0,
      };

      a[v.project.client.id].billableHours = Big(a[v.project.client.id].billableHours)
        .add(v.billableHours || 0)
        .toNumber();

      a[v.project.client.id].nonBillableHours = Big(a[v.project.client.id].nonBillableHours)
        .add(v.nonBillableHours || 0)
        .toNumber();

      a[v.project.client.id].totalHours = Big(a[v.project.client.id].totalHours)
        .add(v.totalHours || 0)
        .toNumber();

      a[v.project.client.id].convertedTotalFees = Big(a[v.project.client.id].convertedTotalFees)
        .add(v.convertedTotalFees || 0)
        .toNumber();

      a[v.project.client.id].projects[v.project.id] = a[v.project.client.id].projects[v.project.id] || {
        project: v.project,
        billableHours: 0,
        nonBillableHours: 0,
        totalHours: 0,
        totalFees: 0,
        convertedTotalFees: 0,
      };

      a[v.project.client.id].projects[v.project.id].billableHours = Big(
        a[v.project.client.id].projects[v.project.id].billableHours,
      )
        .add(v.billableHours || 0)
        .toNumber();

      a[v.project.client.id].projects[v.project.id].nonBillableHours = Big(
        a[v.project.client.id].projects[v.project.id].nonBillableHours,
      )
        .add(v.nonBillableHours || 0)
        .toNumber();

      a[v.project.client.id].projects[v.project.id].totalHours = Big(
        a[v.project.client.id].projects[v.project.id].totalHours,
      )
        .add(v.totalHours || 0)
        .toNumber();

      a[v.project.client.id].projects[v.project.id].totalFees = Big(
        a[v.project.client.id].projects[v.project.id].totalFees,
      )
        .add(v.totalFees || 0)
        .toNumber();

      a[v.project.client.id].projects[v.project.id].convertedTotalFees = Big(
        a[v.project.client.id].projects[v.project.id].convertedTotalFees,
      )
        .add(v.convertedTotalFees || 0)
        .toNumber();

      if (v.role) {
        if (!a[v.project.client.id].projects[v.project.id].roles) {
          a[v.project.client.id].projects[v.project.id].roles = {};
        }

        a[v.project.client.id].projects[v.project.id].roles[v.role.id] = a[v.project.client.id].projects[v.project.id]
          .roles[v.role.id] || {
          role: v.role,
          members: {},
          billableHours: 0,
          nonBillableHours: 0,
          totalHours: 0,
          totalFees: 0,
          convertedTotalFees: 0,
        };

        a[v.project.client.id].projects[v.project.id].roles[v.role.id].hasOverriddenRate =
          a[v.project.client.id].projects[v.project.id].roles[v.role.id].hasOverriddenRate || v.hasOverriddenRate;

        a[v.project.client.id].projects[v.project.id].roles[v.role.id].billableHours = Big(
          a[v.project.client.id].projects[v.project.id].roles[v.role.id].billableHours,
        )
          .add(v.billableHours || 0)
          .toNumber();

        a[v.project.client.id].projects[v.project.id].roles[v.role.id].nonBillableHours = Big(
          a[v.project.client.id].projects[v.project.id].roles[v.role.id].nonBillableHours,
        )
          .add(v.nonBillableHours || 0)
          .toNumber();

        a[v.project.client.id].projects[v.project.id].roles[v.role.id].totalHours = Big(
          a[v.project.client.id].projects[v.project.id].roles[v.role.id].totalHours,
        )
          .add(v.totalHours || 0)
          .toNumber();

        a[v.project.client.id].projects[v.project.id].roles[v.role.id].totalFees = Big(
          a[v.project.client.id].projects[v.project.id].roles[v.role.id].totalFees,
        )
          .add(v.totalFees || 0)
          .toNumber();

        a[v.project.client.id].projects[v.project.id].roles[v.role.id].convertedTotalFees = Big(
          a[v.project.client.id].projects[v.project.id].roles[v.role.id].convertedTotalFees,
        )
          .add(v.convertedTotalFees || 0)
          .toNumber();

        if (v.member) {
          a[v.project.client.id].projects[v.project.id].roles[v.role.id].members[v.member.id] = {
            member: v.member,
            billableHours: v.billableHours,
            nonBillableHours: v.nonBillableHours,
            totalHours: v.totalHours,
            totalFees: v.totalFees,
            convertedTotalFees: v.convertedTotalFees,
          };
        }
      } else {
        if (!a[v.project.client.id].projects[v.project.id].members) {
          a[v.project.client.id].projects[v.project.id].members = {};
        }

        a[v.project.client.id].projects[v.project.id].members[v.member.id] = {
          member: v.member,
          billableHours: v.billableHours,
          nonBillableHours: v.nonBillableHours,
          totalHours: v.totalHours,
          totalFees: v.totalFees,
          effectiveRate: v.effectiveRate,
          hasOverriddenRate: v.hasOverriddenRate,
          convertedTotalFees: v.convertedTotalFees,
        };
      }

      return a;
    }, {});
  }, [report.records]);

  const timeDetail = useTimeEntriesReportURL({ params });

  return (
    <Report.Table>
      <Table>
        <Table.Header sticky>
          <Table.Column sticky name="project:client.name" minWidth="16rem" onSort={onSort} sort={params.sort}>
            Client
          </Table.Column>
          <Table.Column width="10rem" align="right">
            Billable Hours
          </Table.Column>
          <Table.Column width="10rem" align="right">
            Non-Billable Hours
          </Table.Column>
          <Table.Column width="10rem" align="right">
            Total Hours
          </Table.Column>
          <Table.Column width="10rem" align="right">
            <Tooltip message="Revenue divided by hours worked, billable and non-billable, for the period">
              Effective Rate
            </Tooltip>
          </Table.Column>
          <Table.Column width="11rem" align="right">
            Fees
          </Table.Column>
        </Table.Header>

        <Table.Body fade={query.loading.table}>
          {_.map(groups, ({ client, billableHours, nonBillableHours, totalHours, convertedTotalFees, projects }) => {
            return (
              <React.Fragment key={client.id}>
                <Table.GroupRow top="4rem">
                  <Table.Cell>
                    <strong>
                      <ClientInfo>
                        <ClientLink client={client} />
                        {client.isInternal && <InternalClientTooltip />}
                      </ClientInfo>
                    </strong>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>
                      <RouteLink to={timeDetail({ clientId: client.id, timeBillableTypeId: 'billable' })}>
                        <Hours value={billableHours} />
                      </RouteLink>
                    </strong>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>
                      <RouteLink
                        to={timeDetail({ clientId: client.id, timeBillableTypeId: ['non_billable', 'internal'] })}>
                        <Hours value={nonBillableHours} />
                      </RouteLink>
                    </strong>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>
                      <RouteLink to={timeDetail({ clientId: client.id })}>
                        <Hours value={totalHours} />
                      </RouteLink>
                    </strong>
                  </Table.Cell>
                  <Table.Cell />
                  <Table.Cell>
                    <strong>
                      <Currency value={convertedTotalFees} currency={report.currency} />
                    </strong>
                  </Table.Cell>
                </Table.GroupRow>

                {_.map(
                  projects,
                  ({
                    project,
                    billableHours,
                    nonBillableHours,
                    totalHours,
                    totalFees,
                    convertedTotalFees,
                    roles,
                    members,
                  }) => {
                    return (
                      <React.Fragment key={project.id}>
                        <Table.Row>
                          <Table.Cell>
                            <ProjectInfo>
                              <p>
                                <ProjectLink project={project} />
                              </p>
                            </ProjectInfo>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>
                              <RouteLink to={timeDetail({ projectId: project.id, timeBillableTypeId: 'billable' })}>
                                <Hours value={billableHours} />
                              </RouteLink>
                            </strong>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>
                              <RouteLink
                                to={timeDetail({
                                  projectId: project.id,
                                  timeBillableTypeId: ['non_billable', 'internal'],
                                })}>
                                <Hours value={nonBillableHours} />
                              </RouteLink>
                            </strong>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>
                              <RouteLink to={timeDetail({ projectId: project.id })}>
                                <Hours value={totalHours} />
                              </RouteLink>
                            </strong>
                          </Table.Cell>
                          <Table.Cell />
                          <Table.Cell>
                            <p>
                              <strong>
                                <Currency value={convertedTotalFees} currency={report.currency} />
                              </strong>
                              {project.currency !== report.currency && (
                                <Byline>
                                  <Currency value={totalFees} currency={project.currency} />
                                </Byline>
                              )}
                            </p>
                          </Table.Cell>
                        </Table.Row>

                        {_.map(
                          members,
                          ({
                            member,
                            billableHours,
                            nonBillableHours,
                            totalHours,
                            totalFees,
                            effectiveRate,
                            convertedTotalFees,
                            hasOverriddenRate,
                          }) => {
                            return (
                              <Table.Row key={member.id}>
                                <Table.Cell>
                                  <MemberRole>
                                    <BillableIcon value={member.isBillable} />
                                    {member.name}
                                  </MemberRole>
                                </Table.Cell>
                                <Table.Cell>
                                  <RouteLink
                                    to={timeDetail({
                                      projectId: project.id,
                                      memberId: member.id,
                                      timeBillableTypeId: 'billable',
                                    })}>
                                    <Hours value={billableHours} />
                                  </RouteLink>
                                </Table.Cell>
                                <Table.Cell>
                                  <RouteLink
                                    to={timeDetail({
                                      projectId: project.id,
                                      memberId: member.id,
                                      timeBillableTypeId: ['non_billable', 'internal'],
                                    })}>
                                    <Hours value={nonBillableHours} />
                                  </RouteLink>
                                </Table.Cell>
                                <Table.Cell>
                                  <RouteLink to={timeDetail({ projectId: project.id, memberId: member.id })}>
                                    <Hours value={totalHours} />
                                  </RouteLink>
                                </Table.Cell>
                                <Table.Cell>
                                  <Currency value={effectiveRate} currency={project.currency} />
                                </Table.Cell>
                                <Table.Cell>
                                  {hasOverriddenRate && (
                                    <Warning
                                      maxWidth="30rem"
                                      message="One or more time entries have an overridden bill rate.">
                                      <Icon icon="exclamation-triangle" color={colors.warning} />
                                    </Warning>
                                  )}
                                  <p>
                                    <strong>
                                      <Currency value={convertedTotalFees} currency={report.currency} />
                                    </strong>
                                    {project.currency !== report.currency && (
                                      <Byline>
                                        <Currency value={totalFees} currency={project.currency} />
                                      </Byline>
                                    )}
                                  </p>
                                </Table.Cell>
                              </Table.Row>
                            );
                          },
                        )}

                        {_.map(
                          roles,
                          ({
                            role,
                            billableHours,
                            nonBillableHours,
                            totalHours,
                            totalFees,
                            convertedTotalFees,
                            members,
                            hasOverriddenRate,
                          }) => {
                            return (
                              <React.Fragment key={role.id}>
                                <Table.Row>
                                  <Table.Cell>
                                    <MemberRole>
                                      <BillableIcon value={role.isBillable} />
                                      {role.name}
                                    </MemberRole>
                                  </Table.Cell>
                                  <Table.Cell>
                                    <RouteLink
                                      to={timeDetail({
                                        projectId: project.id,
                                        projectRoleId: role.id,
                                        timeBillableTypeId: 'billable',
                                      })}>
                                      <Hours value={billableHours} />
                                    </RouteLink>
                                  </Table.Cell>
                                  <Table.Cell>
                                    <RouteLink
                                      to={timeDetail({
                                        projectId: project.id,
                                        projectRoleId: role.id,
                                        timeBillableTypeId: ['non_billable', 'internal'],
                                      })}>
                                      <Hours value={nonBillableHours} />
                                    </RouteLink>
                                  </Table.Cell>
                                  <Table.Cell>
                                    <RouteLink to={timeDetail({ projectId: project.id, projectRoleId: role.id })}>
                                      <Hours value={totalHours} />
                                    </RouteLink>
                                  </Table.Cell>
                                  <Table.Cell>
                                    <Currency
                                      value={
                                        totalHours
                                          ? Big(totalFees ?? 0)
                                              .div(totalHours)
                                              .toNumber()
                                          : null
                                      }
                                      currency={project.currency}
                                    />
                                  </Table.Cell>
                                  <Table.Cell>
                                    {hasOverriddenRate && (
                                      <Warning
                                        maxWidth="30rem"
                                        message="One or more time entries have an overridden bill rate.">
                                        <Icon icon="exclamation-triangle" color={colors.warning} />
                                      </Warning>
                                    )}
                                    <p>
                                      <strong>
                                        <Currency value={convertedTotalFees} currency={report.currency} />
                                      </strong>
                                      {project.currency !== report.currency && (
                                        <Byline>
                                          <Currency value={totalFees} currency={project.currency} />
                                        </Byline>
                                      )}
                                    </p>
                                  </Table.Cell>
                                </Table.Row>

                                {_.map(
                                  members,
                                  ({
                                    member,
                                    billableHours,
                                    nonBillableHours,
                                    totalHours,
                                    totalFees,
                                    convertedTotalFees,
                                  }) => {
                                    return (
                                      <Table.Row key={`${role.id}_${member.id}`}>
                                        <Table.Cell>
                                          <MemberRole style={{ marginLeft: '3rem' }}>{member.name}</MemberRole>
                                        </Table.Cell>
                                        <Table.Cell>
                                          <RouteLink
                                            to={timeDetail({
                                              projectId: project.id,
                                              projectRoleId: role.id,
                                              memberId: member.id,
                                              timeBillableTypeId: 'billable',
                                            })}>
                                            <Hours value={billableHours} />
                                          </RouteLink>
                                        </Table.Cell>
                                        <Table.Cell>
                                          <RouteLink
                                            to={timeDetail({
                                              projectId: project.id,
                                              projectRoleId: role.id,
                                              memberId: member.id,
                                              timeBillableTypeId: ['non_billable', 'internal'],
                                            })}>
                                            <Hours value={nonBillableHours} />
                                          </RouteLink>
                                        </Table.Cell>
                                        <Table.Cell>
                                          <RouteLink
                                            to={timeDetail({
                                              projectId: project.id,
                                              projectRoleId: role.id,
                                              memberId: member.id,
                                            })}>
                                            <Hours value={totalHours} />
                                          </RouteLink>
                                        </Table.Cell>
                                        <Table.Cell />
                                        <Table.Cell>
                                          <p>
                                            <strong>
                                              <Currency value={convertedTotalFees} currency={report.currency} />
                                            </strong>
                                            {project.currency !== report.currency && (
                                              <Byline>
                                                <Currency value={totalFees} currency={project.currency} />
                                              </Byline>
                                            )}
                                          </p>
                                        </Table.Cell>
                                      </Table.Row>
                                    );
                                  },
                                )}
                              </React.Fragment>
                            );
                          },
                        )}
                      </React.Fragment>
                    );
                  },
                )}
              </React.Fragment>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>
            <Table.Cell>
              <RouteLink
                to={timeDetail({
                  timeBillableTypeId: ['billable'],
                })}>
                <Hours value={report.totals.billableHours} />
              </RouteLink>
            </Table.Cell>
            <Table.Cell>
              <RouteLink
                to={timeDetail({
                  timeBillableTypeId: ['non_billable', 'internal'],
                })}>
                <Hours value={report.totals.nonBillableHours} />
              </RouteLink>
            </Table.Cell>
            <Table.Cell>
              <RouteLink to={timeDetail()}>
                <Hours value={report.totals.totalHours} />
              </RouteLink>
            </Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell>
              <Currency value={report.totals.totalFees} currency={report.currency} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status
          total={_.size(groups)}
          label="Client"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <YesNoFilter
        icon="filter"
        placeholder="Include members and roles without time"
        value={filters.includeMembersAndRolesWithoutTime ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ includeMembersAndRolesWithoutTime: value === 'yes' })}
      />

      <Checkbox
        checked={filters.includeMembersAndRolesWithoutTime}
        label="Include members and roles without tracked time"
        onChange={({ target: { checked } }) => handleFilter({ includeMembersAndRolesWithoutTime: checked })}
      />

      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
